import { useTranslation } from "react-i18next";
import { AlertTriangle } from "lucide-react";
import { Typography } from "@akuna/ui";
import { MultipleAutocomplete, type AutocompleteOption } from "@/components/ui/autocomplete";
import { useReservationPacks } from "./useReservationPacks";
import { colorDotClass, colorDotStyle } from "../accommodations/colors";

type ReservationPackOption = AutocompleteOption & {
  pack: (ReturnType<typeof useReservationPacks>["activePacks"])[number];
};

export function ReservationPackSelect({ date, disabled = false, onChange, petId, value }: { date: string; disabled?: boolean; onChange: (value: string[]) => void; petId: string | null; value: string[] }) {
  const { t } = useTranslation("daycareHotel");
  const { activePacks, isLoading } = useReservationPacks({ date, petId });
  const options: ReservationPackOption[] = activePacks.map((pack) => ({
    value: pack.id,
    label: pack.name,
    pack,
  }));

  return (
    <div className="space-y-2">
      <label className="text-sm font-medium text-foreground">{t("pack")}</label>
      <MultipleAutocomplete
      disabled={disabled || !date || isLoading}
      options={options}
      values={value}
      onChange={onChange}
      placeholder={!date ? t("selectDateFirst") : t("noActivePacks")}
      emptyMessage={t("noActivePacks")}
      removeLabel={t("petForm.packsRemove")}
      ariaLabel={t("pack")}
      popoverPortalled={false}
      optionClassName="group flex items-center gap-2 py-2"
      renderSelectedOption={(option) => {
        const pack = (option as ReservationPackOption).pack;
        return (
          <>
            <span className={`h-2.5 w-2.5 shrink-0 rounded-full ${colorDotClass(pack.color)}`} style={colorDotStyle(pack.color)} aria-hidden="true" />
            <span className="min-w-0 truncate">{pack.name}</span>
          </>
        );
      }}
      renderOption={(option, selected) => {
        const pack = (option as ReservationPackOption).pack;
        return (
          <span className="flex min-w-0 items-center gap-2 text-sm">
            <span className={`h-2.5 w-2.5 shrink-0 rounded-full ${colorDotClass(pack.color)}`} style={colorDotStyle(pack.color)} aria-hidden="true" />
            <span className="min-w-0 truncate">{pack.name}</span>
            <Typography asChild variant="caption">
              <span className="shrink-0">· {pack.memberCount} {t("petsLabel")}</span>
            </Typography>
            {pack.incompatibilityCount > 0 ? (
              <span className="ml-auto flex shrink-0 items-center gap-1" aria-label={t("packIncompatibility", { count: pack.incompatibilityCount })}>
                <AlertTriangle size={13} className="text-destructive" aria-hidden="true" />
                <Typography asChild variant="caption">
                  <span className="text-destructive">{t("packIncompatibility", { count: pack.incompatibilityCount })}</span>
                </Typography>
              </span>
            ) : null}
            {selected ? <span aria-hidden="true" className="ml-auto text-primary">✓</span> : null}
          </span>
        );
      }}
      />
    </div>
  );
}
